import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  studyLogin as apiLogin,
  getStudyToken,
  getStudyEmail,
  setStudyAuth,
  getStudyLicense,
  setStudyLicense,
  getStudyMe,
  requestStudentSignInCode,
  verifyStudentSignInCode,
} from "@/lib/api";

const StudyAuthContext = createContext(null);

export function StudyAuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [token, setToken] = useState(() => getStudyToken());
  const [email, setEmail] = useState(() => getStudyEmail());
  const [license, setLicense] = useState(() => getStudyLicense());

  const applyAuthResponse = useCallback((r) => {
    setStudyAuth(r.token, r.email);
    setToken(r.token);
    setEmail(r.email);
    if (r.license) {
      setStudyLicense(r.license);
      setLicense(r.license);
    }
  }, []);

  const login = useCallback(async (em, code) => {
    const r = await apiLogin(em, code);
    applyAuthResponse(r);
    return r;
  }, [applyAuthResponse]);

  const requestSignInCode = useCallback(
    (em, firstName = "") => requestStudentSignInCode(em, firstName),
    []
  );

  const verifySignInCode = useCallback(async (em, code) => {
    const r = await verifyStudentSignInCode(em, code);
    applyAuthResponse(r);
    return r;
  }, [applyAuthResponse]);

  const logout = useCallback(() => {
    setStudyAuth("", "");
    setToken("");
    setEmail("");
    setLicense(null);
  }, []);

  const refreshAccess = useCallback(async () => {
    if (!token) return null;
    let me;
    try {
      me = await getStudyMe();
    } catch (error) {
      if ([401, 403].includes(error?.response?.status)) logout();
      throw error;
    }
    if (!me) return null;
    const lic = {
      first_name: me.first_name || "",
      email: me.email,
      order_id: me.order_id,
      code_id: me.code_id,
      account_id: me.account_id || "",
      access_level: me.access_level || "full",
      has_full_access: me.has_full_access !== false,
      auth_method: me.auth_method || "legacy_access_code",
    };
    setStudyLicense(lic);
    setLicense(lic);
    if (license?.has_full_access !== lic.has_full_access) {
      queryClient.invalidateQueries({
        predicate: (query) => String(query.queryKey?.[0] || "").startsWith("study-") ||
          ["learning-progress", "flashcards", "word-bank-question-index"].includes(query.queryKey?.[0]),
      });
    }
    return lic;
  }, [token, license?.has_full_access, queryClient, logout]);

  // Rehydrate account and entitlement details after a refresh or direct deep link.
  useEffect(() => {
    let cancelled = false;
    const needsLicenseRefresh =
      token &&
      (!license ||
        !Object.prototype.hasOwnProperty.call(license, "first_name") ||
        !Object.prototype.hasOwnProperty.call(license, "has_full_access"));
    if (needsLicenseRefresh) {
      refreshAccess()
        .then(() => {
          if (cancelled) return;
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [token, license, refreshAccess]);

  // Checkout opens in a new tab. Refresh entitlement state as soon as the
  // student returns so a paid order unlocks without another login.
  useEffect(() => {
    if (!token) return undefined;
    const onFocus = () => refreshAccess().catch(() => {});
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [token, refreshAccess]);

  // Stay locked until the server-confirmed entitlement is present. This avoids
  // a paid-page flash when an older session has no cached account metadata yet.
  const accessLevel = license?.access_level || "free";
  const hasFullAccess = Boolean(token) && license?.has_full_access === true && accessLevel === "full";

  useEffect(() => {
    if (!token || hasFullAccess) return undefined;
    const timer = window.setInterval(() => refreshAccess().catch(() => {}), 15000);
    return () => window.clearInterval(timer);
  }, [token, hasFullAccess, refreshAccess]);

  const value = useMemo(
    () => ({
      token,
      email,
      license,
      accessLevel,
      hasFullAccess,
      isFree: Boolean(token) && !hasFullAccess,
      isAuthed: Boolean(token),
      login,
      requestSignInCode,
      verifySignInCode,
      refreshAccess,
      logout,
    }),
    [
      token,
      email,
      license,
      accessLevel,
      hasFullAccess,
      login,
      requestSignInCode,
      verifySignInCode,
      refreshAccess,
      logout,
    ]
  );

  return (
    <StudyAuthContext.Provider value={value}>{children}</StudyAuthContext.Provider>
  );
}

export function useStudyAuth() {
  const ctx = useContext(StudyAuthContext);
  if (!ctx) throw new Error("useStudyAuth must be used within a StudyAuthProvider");
  return ctx;
}

export default StudyAuthContext;
